import React, { useMemo, useState } from 'react';
import {
  BrioModal, BrioModalBody, BrioModalField, BrioInput, BrioSelect, BrioButton,
} from '../../components/BrioComponents';
import useBoqCreateCustomSubtrade from '../hooks/useBoqCreateCustomSubtrade';

// Add a standardized SD allowance line by picking a sub-trade from the
// canonical catalogue (boq.trade_template_lines, via useBoqSubtradeCatalogue)
// rather than typing free text. Picking a sub-trade fills in the canonical
// trade + a default unit; the line is written with those canonical labels so
// it lines up with the SD rate corpus, the Summary roll-up, and cross-project
// usage counts by construction.
//
// If the catalogue doesn't have the sub-trade you need, the "＋ Add a new
// sub-trade…" option lets you create one inline — it goes through the same
// governed create_custom_subtrade path (dedup guard + is_custom + admin review)
// as the standalone editor, with a similarity nudge to discourage near-dupes,
// so the catalogue stays clean.

const NEW = '__new__';

const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

// Small substring + word-overlap similarity — same heuristic as the standalone
// custom-subtrade modal, so the "looks similar to…" nudge is consistent.
function isSimilar(a, b) {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.length >= 4 && nb.includes(na)) return true;
  if (nb.length >= 4 && na.includes(nb)) return true;
  const wa = new Set(na.split(' ').filter((w) => w.length >= 3));
  const wb = new Set(nb.split(' ').filter((w) => w.length >= 3));
  let overlap = 0;
  for (const w of wa) if (wb.has(w)) overlap += 1;
  return overlap >= 2;
}

function AddAllowanceLineModal({
  catalogue = [],
  catalogueLoading = false,
  zones = [],
  defaultSequenceOrder = 0,
  onClose,
  onCreate,
  onSubtradeCreated,
}) {
  const [subtradeNodeId, setSubtradeNodeId] = useState('');
  const [unit, setUnit] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [rate, setRate] = useState('');
  const [description, setDescription] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [newName, setNewName] = useState('');
  const [newChapter, setNewChapter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { createCustomSubtrade } = useBoqCreateCustomSubtrade();

  const isNew = subtradeNodeId === NEW;

  // Sub-trades grouped by their parent trade for an <optgroup> picker, in the
  // catalogue's canonical sort order (trades stay contiguous because sort_order
  // is block-allocated per trade).
  const groups = useMemo(() => {
    const order = [];
    const byTrade = new Map();
    [...catalogue]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .forEach((e) => {
        if (!byTrade.has(e.tradeLabel)) { byTrade.set(e.tradeLabel, []); order.push(e.tradeLabel); }
        byTrade.get(e.tradeLabel).push(e);
      });
    return order.map((trade) => ({ trade, entries: byTrade.get(trade) }));
  }, [catalogue]);

  // The canonical trades (chapters), in order — the parents a new sub-trade can
  // be filed under.
  const chapters = useMemo(() => groups.map((g) => g.trade), [groups]);

  const selected = useMemo(
    () => (isNew ? null : catalogue.find((e) => e.subtradeNodeId === subtradeNodeId) || null),
    [catalogue, subtradeNodeId, isNew]
  );

  // Existing sub-trades under the chosen chapter that look like the typed name.
  const similar = useMemo(() => {
    if (!isNew || !newChapter || newName.trim().length < 2) return [];
    return catalogue
      .filter((e) => e.tradeLabel === newChapter && isSimilar(e.subtradeLabel, newName))
      .slice(0, 4);
  }, [isNew, newChapter, newName, catalogue]);

  const handlePick = (e) => {
    const id = e.target.value;
    setSubtradeNodeId(id);
    if (id !== NEW) {
      // Auto-fill the unit from the catalogue default; the user can still override.
      const entry = catalogue.find((x) => x.subtradeNodeId === id);
      setUnit(entry ? entry.defaultUnit : '');
    }
  };

  const canSubmit = !!subtradeNodeId && (!isNew || (newName.trim().length >= 2 && !!newChapter));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (rate !== '' && (isNaN(Number(rate)) || Number(rate) < 0)) { setError('Enter a valid rate.'); return; }
    if (quantity !== '' && (isNaN(Number(quantity)) || Number(quantity) < 0)) { setError('Enter a valid quantity.'); return; }
    if (!unit.trim()) { setError('Unit is required.'); return; }

    setSubmitting(true);
    let tradeLabel;
    let subtradeLabel;
    if (isNew) {
      const name = newName.trim();
      if (name.length < 2) { setSubmitting(false); setError('Enter a sub-trade name (min 2 characters).'); return; }
      if (!newChapter) { setSubmitting(false); setError('Pick a trade for the new sub-trade.'); return; }
      const created = await createCustomSubtrade({ tradeLabel: newChapter, subtradeLabel: name, defaultUnit: unit.trim() });
      if (!created.success) { setSubmitting(false); setError(created.error || 'Failed to add the sub-trade.'); return; }
      tradeLabel = created.result.trade_label;
      subtradeLabel = created.result.subtrade_label;
      // Refresh the catalogue so the new sub-trade is pickable next time.
      if (typeof onSubtradeCreated === 'function') { try { await onSubtradeCreated(); } catch (_) { /* non-fatal */ } }
    } else {
      if (!selected) { setSubmitting(false); setError('Pick a sub-trade.'); return; }
      tradeLabel = selected.tradeLabel;
      subtradeLabel = selected.subtradeLabel;
    }

    const result = await onCreate({
      itemVersionId: null,
      lineType: 'allowance',
      trade: tradeLabel,
      subtrade: subtradeLabel,
      unit: unit.trim(),
      quantity: quantity === '' ? 0 : Number(quantity),
      rateQuoted: rate === '' ? null : Number(rate),
      descriptionOverride: description.trim() || null,
      projectZoneId: zoneId || null,
      sequenceOrder: defaultSequenceOrder,
    });
    setSubmitting(false);
    if (result?.success) onClose();
    else setError(result?.error || 'Failed to add line.');
  };

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleSubmit} disabled={submitting || !canSubmit}>
        {submitting ? 'Adding…' : (isNew ? 'Add sub-trade & line' : 'Add line')}
      </BrioButton>
    </>
  );

  return (
    <BrioModal title="Add allowance line" onClose={onClose} width="md" footer={footer}>
      <BrioModalBody form>
        {error && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>{error}</div>
        )}
        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--brio-surface-muted, #f7f7f5)', borderRadius: 6, fontSize: '0.85rem' }}>
          Pick a standardized sub-trade — the line inherits the canonical trade and a default unit, and lines up with the SD rate history and Summary automatically.
          <div style={{ color: 'var(--brio-text-muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Not in the list? Choose <strong>＋ Add a new sub-trade…</strong> at the top — it's added to the standard catalogue (flagged for review), not typed as free text.
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <BrioModalField label="Sub-trade *">
            <BrioSelect value={subtradeNodeId} onChange={handlePick} autoFocus disabled={catalogueLoading}>
              <option value="">{catalogueLoading ? 'Loading catalogue…' : 'Select a sub-trade…'}</option>
              <option value={NEW}>＋ Add a new sub-trade…</option>
              {groups.map(({ trade, entries }) => (
                <optgroup key={trade} label={trade}>
                  {entries.map((e) => (
                    <option key={e.subtradeNodeId} value={e.subtradeNodeId}>
                      {e.subtradeLabel}{e.isCustom ? ' (custom)' : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </BrioSelect>
          </BrioModalField>

          {isNew ? (
            <div style={{ margin: '-0.2rem 0 1rem 0', padding: '0.85rem', border: '1px dashed var(--brio-border, #ccc)', borderRadius: 8 }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted)', marginBottom: '0.6rem' }}>
                New sub-trade — added to the canonical catalogue under one of the 11 trades, visible to every project and queued for admin review.
              </div>
              <BrioModalField label="New sub-trade name *" hint="Short, human-readable. Use sentence case.">
                <BrioInput value={newName} onChange={(ev) => setNewName(ev.target.value)} placeholder="e.g. Curved Glazing" maxLength={80} />
              </BrioModalField>
              <BrioModalField label="Trade *" hint="Which of the 11 canonical trades it belongs under.">
                <BrioSelect value={newChapter} onChange={(ev) => setNewChapter(ev.target.value)}>
                  <option value="">Select a trade…</option>
                  {chapters.map((c) => <option key={c} value={c}>{c}</option>)}
                </BrioSelect>
              </BrioModalField>
              {similar.length > 0 && (
                <div style={{ margin: '0 0 0.3rem 0', padding: '0.6rem 0.85rem', background: '#fff4d6', color: '#7a5a00', borderRadius: 6, fontSize: '0.82rem' }}>
                  Looks similar to{' '}
                  {similar.map((s, i) => (
                    <React.Fragment key={s.subtradeNodeId}>
                      <strong>{s.subtradeLabel}</strong>{i < similar.length - 1 ? ', ' : ''}
                    </React.Fragment>
                  ))}
                  . Consider using the existing one instead.
                </div>
              )}
            </div>
          ) : (
            selected && (
              <div style={{ margin: '-0.4rem 0 1rem 0', fontSize: '0.8rem', color: 'var(--brio-text-muted)' }}>
                Trade: <strong>{selected.tradeLabel}</strong>
              </div>
            )
          )}

          <BrioModalField label="Description" hint="Optional — a line caption. Defaults to the sub-trade name.">
            <BrioInput value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder={selected ? `e.g. ${selected.subtradeLabel} — per design` : 'e.g. Waterproofing — wet areas'} />
          </BrioModalField>
          <BrioModalField label="Unit *" hint={isNew ? 'Becomes the new sub-trade’s default unit too.' : 'Pre-filled from the sub-trade’s default.'}>
            <BrioInput value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="e.g. sqm" />
          </BrioModalField>
          <BrioModalField label="Quantity">
            <BrioInput type="number" step="any" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </BrioModalField>
          <BrioModalField label="Rate (estimate)" hint="Leave blank to add a placeholder and price it later.">
            <BrioInput type="number" step="any" min="0" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="e.g. 1200" />
          </BrioModalField>
          {zones.length > 0 && (
            <BrioModalField label="Zone" hint="Optional — which block/zone this line belongs to.">
              <BrioSelect value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
                <option value="">— None —</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>{z.code ? `${z.code} · ${z.label}` : z.label}</option>
                ))}
              </BrioSelect>
            </BrioModalField>
          )}
        </form>
      </BrioModalBody>
    </BrioModal>
  );
}

export default AddAllowanceLineModal;
